import { NamingConfig } from '@shared/naming/naming-config';

export const AUTH_TYPE_API_KEY = 'api-key';
export const AUTH_TYPE_BEARER_TOKEN = 'bearer-token';
export const AUTH_TYPE_OAUTH2_CC = 'oauth2-client-credentials';
export const AUTH_TYPE_SMART_BACKEND = 'smart-backend-services';
export const AUTH_TYPE_USERNAME_PASSWORD = 'username-password';
export const AUTH_TYPE_SSH_KEY = 'ssh-key';

export type AuthType =
  | typeof AUTH_TYPE_API_KEY
  | typeof AUTH_TYPE_BEARER_TOKEN
  | typeof AUTH_TYPE_OAUTH2_CC
  | typeof AUTH_TYPE_SMART_BACKEND
  | typeof AUTH_TYPE_USERNAME_PASSWORD
  | typeof AUTH_TYPE_SSH_KEY;

/** UI label for an auth type. */
export const AUTH_TYPE_LABELS: Record<AuthType, string> = {
  [AUTH_TYPE_API_KEY]: $localize`API Key`,
  [AUTH_TYPE_BEARER_TOKEN]: $localize`Bearer Token`,
  [AUTH_TYPE_OAUTH2_CC]: $localize`OAuth 2.0 Client Credentials`,
  [AUTH_TYPE_SMART_BACKEND]: $localize`SMART Backend Services`,
  [AUTH_TYPE_USERNAME_PASSWORD]: $localize`Username / Password`,
  [AUTH_TYPE_SSH_KEY]: $localize`SSH Private Key`,
};

/** Field schema for an auth type. */
export interface AuthFieldSpec {
  /** Field key inside the merged auth-config JSON object. */
  key: string;
  /** Form label. */
  label: string;
  /** True if the field is encrypted server-side (lives in Securable, never returned to UI). */
  secret: boolean;
  /** Renders as multi-line textarea instead of a single-line input. */
  multiline?: boolean;
  /** Input placeholder hint. */
  placeholder?: string;
}

export const AUTH_TYPE_FIELDS: Record<AuthType, AuthFieldSpec[]> = {
  [AUTH_TYPE_API_KEY]: [
    { key: 'apiKeyName', label: $localize`Key Name`, secret: false, placeholder: 'ApiKey' },
    { key: 'apiKeyValue', label: $localize`Key Value`, secret: true },
  ],
  [AUTH_TYPE_BEARER_TOKEN]: [
    { key: 'token', label: $localize`Bearer Token`, secret: true, multiline: true },
  ],
  [AUTH_TYPE_OAUTH2_CC]: [
    { key: 'tokenUrl', label: $localize`Token URL`, secret: false, placeholder: 'https://issuer.example.com/oauth/token' },
    { key: 'clientId', label: $localize`Client ID`, secret: false },
    { key: 'clientSecret', label: $localize`Client Secret`, secret: true },
    { key: 'scope', label: $localize`Scope (optional)`, secret: false, placeholder: 'read write' },
    { key: 'audience', label: $localize`Audience (optional)`, secret: false },
  ],
  [AUTH_TYPE_SMART_BACKEND]: [
    { key: 'tokenUrl', label: $localize`Token URL`, secret: false, placeholder: 'https://fhir.example.com/oauth2/token' },
    { key: 'clientId', label: $localize`Client ID`, secret: false },
    { key: 'scope', label: $localize`Scope (optional)`, secret: false, placeholder: 'system/*.read' },
    { key: 'audience', label: $localize`Audience (optional)`, secret: false, placeholder: 'https://fhir.example.com' },
    { key: 'jwtAlg', label: $localize`JWT Algorithm (optional)`, secret: false, placeholder: 'RS384' },
    { key: 'privateKey', label: $localize`Private Key (PEM)`, secret: true, multiline: true, placeholder: '-----BEGIN PRIVATE KEY-----' },
  ],
  [AUTH_TYPE_USERNAME_PASSWORD]: [
    { key: 'username', label: $localize`Username`, secret: false },
    { key: 'password', label: $localize`Password`, secret: true },
  ],
  [AUTH_TYPE_SSH_KEY]: [
    { key: 'username', label: $localize`Username`, secret: false },
    { key: 'privateKey', label: $localize`Private Key (PEM)`, secret: true, multiline: true, placeholder: '-----BEGIN RSA PRIVATE KEY-----' },
    { key: 'passphrase', label: $localize`Key Password (optional)`, secret: true },
    { key: 'publicKey', label: $localize`Public Key (optional)`, secret: false, multiline: true, placeholder: 'ssh-rsa AAAA...' },
  ],
};

/** Sentinel value the UI shows for secret fields when the server already has them stored. */
export const SECRET_PLACEHOLDER = '***';

export type AuthConfig = {
  id: string;
  customerId: string;
  name: string;
  authType: AuthType | string;
  /** True when this config is shared read-only with every customer. */
  isGlobal?: boolean;
  /** Non-secret fields + (for SMART) publicJwk, JSON-encoded. Secrets never appear here. */
  configJson?: string | null;
  /** Opaque securable id; presence indicates secrets are stored. */
  securableId?: string | null;
  createdUtc?: string;
  createdBy?: string;
  updatedUtc?: string;
  updatedBy?: string;
  deletedUtc?: string;
  deletedBy?: string;
};

export const AuthConfigNaming = NamingConfig<AuthConfig>({
  key: 'AuthConfig',
  modelName: { '=1': $localize`Auth Config`, other: $localize`Auth Configs` },
  fieldNames: {
    id: $localize`ID`,
    customerId: $localize`Customer ID`,
    name: $localize`Name`,
    authType: $localize`Auth Type`,
    isGlobal: $localize`Global`,
    configJson: $localize`Configuration`,
    securableId: $localize`Secret Storage ID`,
    createdUtc: $localize`Created`,
    createdBy: $localize`Created By`,
    updatedUtc: $localize`Updated`,
    updatedBy: $localize`Updated By`,
    deletedUtc: $localize`Deleted`,
    deletedBy: $localize`Deleted By`,
  },
});
